import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleAdaptiveNavbar from '../../components/ui/RoleAdaptiveNavbar';
import GalleryNavigationBreadcrumb from '../../components/ui/GalleryNavigationBreadcrumb';
import NotificationIndicator from '../../components/ui/NotificationIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MediaGridItem from './components/MediaGridItem';
import GoogleDriveSyncPanel from './components/GoogleDriveSyncPanel';
import GallerySettingsPanel from './components/GallerySettingsPanel';
import VisitorAnalyticsPanel from './components/VisitorAnalyticsPanel';
import BulkActionsBar from './components/BulkActionsBar';
import MediaFilterBar from './components/MediaFilterBar';
import EditMediaModal from './components/EditMediaModal';
import { galleryService } from '../../services/galleryService';
import { mediaService } from '../../services/mediaService';
import { googleDriveService } from '../../services/googleDriveService';

const GalleryManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const userRole = userProfile?.role || 'contributor';
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [viewMode, setViewMode] = useState('grid');
  const [editingMedia, setEditingMedia] = useState(null);
  const [activeTab, setActiveTab] = useState('media');
  const [gallery, setGallery] = useState(null);
  const [mediaData, setMediaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const galleryId = searchParams?.get('id');

  useEffect(() => {
    if (galleryId) {
      loadGalleryData();
    }
  }, [galleryId]);

  const loadGalleryData = async () => {
    setLoading(true);
    try {
      const [galleryData, media] = await Promise.all([
        galleryService?.getGalleryById(galleryId),
        mediaService?.getGalleryMedia(galleryId, { sortBy, mediaType: filterType })
      ]);
      setGallery(galleryData);
      setMediaData(media);
    } catch (error) {
      console.error('Error loading gallery data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async () => {
    if (!gallery?.drive_compressed_folder_id) {
      alert('No Google Drive folder configured for this gallery');
      return;
    }

    setSyncing(true);
    try {
      const result = await mediaService?.syncFolderMedia(
        galleryId,
        gallery?.drive_compressed_folder_id,
        'compressed'
      );
      
      alert(`Sync complete! ${result?.synced} new files added out of ${result?.total} total files.`);
      await loadGalleryData(); // Reload media
    } catch (error) {
      console.error('Sync error:', error);
      alert('Failed to sync with Google Drive. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event?.target?.files);
    if (files?.length === 0) return;

    if (!gallery?.drive_compressed_folder_id) {
      alert('No Google Drive folder configured for this gallery');
      return;
    }

    // Check if signed in to Google Drive
    const isSignedIn = await googleDriveService?.checkSignInStatus();
    if (!isSignedIn) {
      try {
        await googleDriveService?.signIn();
      } catch (error) {
        alert('Please sign in to Google Drive to upload files');
        return;
      }
    }

    setUploadingFiles(true);
    try {
      const uploadPromises = files?.map(file => 
        mediaService?.uploadMedia(
          file,
          galleryId,
          gallery?.drive_compressed_folder_id,
          { downloadEnabled: true }
        )
      );

      await Promise.all(uploadPromises);
      alert(`Successfully uploaded ${files?.length} file(s)`);
      await loadGalleryData(); // Reload media
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload some files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleMediaSelect = (mediaId) => {
    setSelectedMedia((prev) =>
    prev?.includes(mediaId) ?
    prev?.filter((id) => id !== mediaId) :
    [...prev, mediaId]
    );
  };

  const handleSelectAll = () => {
    setSelectedMedia(mediaData?.map((m) => m?.id));
  };

  const handleDeselectAll = () => {
    setSelectedMedia([]);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Delete ${selectedMedia?.length} selected items?`)) {
      try {
        await mediaService?.bulkDeleteMedia(selectedMedia);
        setSelectedMedia([]);
        await loadGalleryData();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Failed to delete some files');
      }
    }
  };

  const handleBulkDownloadToggle = async () => {
    try {
      const enabled = window.confirm('Enable downloads for selected media?');
      await Promise.all(
        selectedMedia?.map(id => mediaService?.toggleDownload(id, enabled))
      );
      await loadGalleryData();
    } catch (error) {
      console.error('Bulk toggle error:', error);
    }
  };

  const handleBulkExport = () => {
    alert('Bulk export feature coming soon');
  };

  const handleEditMedia = (media) => {
    setEditingMedia(media);
  };

  const handleDeleteMedia = async (mediaId) => {
    if (window.confirm('Delete this media file?')) {
      try {
        await mediaService?.deleteMedia(mediaId);
        await loadGalleryData();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete file');
      }
    }
  };

  const handleToggleDownload = async (mediaId) => {
    try {
      const media = mediaData?.find(m => m?.id === mediaId);
      await mediaService?.toggleDownload(mediaId, !media?.download_enabled);
      await loadGalleryData();
    } catch (error) {
      console.error('Toggle download error:', error);
    }
  };

  const handleSaveMedia = async (updatedMedia) => {
    try {
      await mediaService?.updateMedia(updatedMedia?.id, {
        title: updatedMedia?.title,
        description: updatedMedia?.description,
        altText: updatedMedia?.alt_text,
        downloadEnabled: updatedMedia?.download_enabled
      });
      setEditingMedia(null);
      await loadGalleryData();
    } catch (error) {
      console.error('Save media error:', error);
      alert('Failed to save changes');
    }
  };

  const handleConfigureSync = () => {
    navigate(`/gallery-creation?edit=${galleryId}`);
  };

  const handleUpdateSettings = async (settings) => {
    try {
      await galleryService?.updateGallery(galleryId, {
        showMediaInfo: settings?.showMediaInfo,
        downloadEnabled: settings?.downloadEnabled,
        isPublished: settings?.isPublic
      });
      await loadGalleryData();
    } catch (error) {
      console.error('Update settings error:', error);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location?.origin}/gallery-viewer?id=${galleryId}`;
    navigator.clipboard?.writeText(link);
    alert('Gallery link copied to clipboard!');
  };

  const handleRegenerateLink = () => {
    alert('Link regeneration feature coming soon');
  };

  const handleExportVisitorData = () => {
    alert('Visitor data export feature coming soon');
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Mark notification as read:', notificationId);
  };

  const handleMarkAllAsRead = () => {
    console.log('Mark all notifications as read');
  };

  // Filter media based on search and filters
  const filteredMedia = mediaData?.filter(media => {
    const matchesSearch = media?.file_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         media?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesType = filterType === 'all' || media?.media_type === filterType;
    return matchesSearch && matchesType;
  });

  const mockNotifications = [
  {
    id: 1,
    type: 'sync',
    title: 'Google Drive Sync Complete',
    message: '8 new media files added to gallery',
    timestamp: new Date(Date.now() - 300000),
    read: false
  },
  {
    id: 2,
    type: 'visitor',
    title: 'New Visitor Access',
    message: 'Sarah Johnson viewed your gallery',
    timestamp: new Date(Date.now() - 1800000),
    read: false
  },
  {
    id: 3,
    type: 'gallery',
    title: 'Download Activity',
    message: '23 files downloaded in the last hour',
    timestamp: new Date(Date.now() - 3600000),
    read: true
  }];


  const breadcrumbItems = [
  { label: 'Dashboard', path: '/contributor-dashboard', icon: 'LayoutDashboard' },
  { label: 'Gallery Management', icon: 'Images' }];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">Gallery Not Found</h2>
          <Button onClick={() => navigate('/contributor-dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleAdaptiveNavbar userRole={userRole} userName="Contributor" />
      <div className="pt-16 md:pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <GalleryNavigationBreadcrumb items={breadcrumbItems} userRole={userRole} />

          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mt-4 md:mt-6 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-2">{gallery?.gallery_name || 'Gallery Management'}</h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage media files, sync with Google Drive, and track visitor engagement
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3 w-full lg:w-auto">
              <NotificationIndicator notifications={mockNotifications} />
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/*,video/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={() => document.getElementById('file-upload')?.click()}
                loading={uploadingFiles}
                className="flex-1 lg:flex-none">
                Upload Media
              </Button>
              <Button
                variant="default"
                iconName="Eye"
                iconPosition="left"
                onClick={() => navigate(`/gallery-viewer?id=${galleryId}`)}
                className="flex-1 lg:flex-none">
                Preview Gallery
              </Button>
            </div>
          </div>

          <div className="lg:hidden mb-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['media', 'sync', 'settings', ...(userRole === 'owner' ? ['analytics'] : [])]?.map((tab) =>
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab ?
                'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground hover:bg-muted/80'}`
                }>

                  {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)}
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className={`lg:col-span-2 space-y-4 md:space-y-6 ${activeTab !== 'media' && 'hidden lg:block'}`}>
              <MediaFilterBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filterType={filterType}
                onFilterTypeChange={setFilterType}
                sortBy={sortBy}
                onSortChange={setSortBy}
                viewMode={viewMode}
                onViewModeChange={setViewMode} />


              {selectedMedia?.length > 0 &&
              <BulkActionsBar
                selectedCount={selectedMedia?.length}
                totalCount={filteredMedia?.length}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onBulkDelete={handleBulkDelete}
                onBulkDownloadToggle={handleBulkDownloadToggle}
                onBulkExport={handleBulkExport} />

              }

              {filteredMedia?.length === 0 ?
              <div className="bg-card rounded-xl border border-border p-8 md:p-12 text-center">
                  <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No media found</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Try adjusting your search or filter criteria
                  </p>
                  <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setFilterType('all');
                  }}>

                    Clear Filters
                  </Button>
                </div> :

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  {filteredMedia?.map((media) =>
                <MediaGridItem
                  key={media?.id}
                  media={media}
                  isSelected={selectedMedia?.includes(media?.id)}
                  onSelect={handleMediaSelect}
                  onEdit={handleEditMedia}
                  onDelete={handleDeleteMedia}
                  onToggleDownload={handleToggleDownload} />

                )}
                </div>
              }
            </div>

            <div className="space-y-4 md:space-y-6">
              <div className={activeTab !== 'sync' && 'hidden lg:block'}>
                <GoogleDriveSyncPanel
                  syncStatus={syncing ? 'syncing' : 'synced'}
                  lastSyncTime={gallery?.last_sync_at ? new Date(gallery.last_sync_at) : new Date()}
                  folderPath={gallery?.drive_compressed_folder_name || 'Not configured'}
                  onManualSync={handleManualSync}
                  onConfigureSync={handleConfigureSync} />

              </div>

              <div className={activeTab !== 'settings' && 'hidden lg:block'}>
                <GallerySettingsPanel
                  galleryLink={`${window.location?.origin}/gallery-viewer?id=${galleryId}`}
                  expirationDate="2026-03-31"
                  isPublic={gallery?.is_published}
                  requiresAuth={gallery?.require_media_metadata}
                  showMediaInfo={gallery?.show_media_info}
                  onUpdateSettings={handleUpdateSettings}
                  onCopyLink={handleCopyLink}
                  onRegenerateLink={handleRegenerateLink} />

              </div>
            </div>
          </div>

          <div className={`mt-6 md:mt-8 ${activeTab !== 'analytics' && 'hidden lg:block'}`}>
            {userRole === 'owner' && (
              <VisitorAnalyticsPanel
                visitors={[]}
                onExportData={handleExportVisitorData} />
            )}
          </div>
        </div>
      </div>
      {editingMedia &&
      <EditMediaModal
        media={editingMedia}
        onClose={() => setEditingMedia(null)}
        onSave={handleSaveMedia} />

      }
    </div>
  );

};

export default GalleryManagement;